import { logger } from '@adornis/base/logging.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import type { Slot } from '@ils/slots/db/slot.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { firstValueFrom, of, switchMap, timer } from 'rxjs';
import type { ExamDay } from '../../db/examdays.js';
import { ILSUser } from '../../db/ils-user.js';

/**
 * @element ils-replace-tester-dialog
 */
@customElement('ils-replace-tester-dialog')
export class IlsReplaceTesterDialog extends XDialog<boolean> {
  static override get element_name() {
    return 'ils-replace-tester-dialog';
  }

  @property({ attribute: false }) examDay = new RXController<Maybe<ExamDay>>(this, undefined);
  @property({ attribute: false }) examSlot?: Slot;
  @property({ attribute: false }) allSlots: Slot[] = [];

  @state() private readonly _fallbackTester = new RXController(this, this.examDay.observable, examDay$ =>
    examDay$.pipe(
      switchMap(examDay => {
        if (!examDay?.fallbackTesters.length) return of(null);
        return getByID(
          ILSUser,
          examDay.fallbackTesters[examDay.getRoundNumberForSlot(this.examSlot)],
        )({
          ecommerce: { firstName: 1, lastName: 1 },
          username: 1,
        });
      }),
    ),
  );

  private _selectedTester?: ILSUser;
  private _replaceAllFutureSlots = false;

  override render() {
    if (!this.examSlot || !this.examDay.value) return '404 - ExamDay oder Slot nicht gefunden';
    return html`
      <x-flex padding="md" space="lg">
        <x-h3> Ersatzschauspieler einsetzen </x-h3>
        <x-flex space="md">
          <x-dropdown-selection
            .selectables=${this._fallbackTester.value ? [this._fallbackTester.value] : []}
            .renderString=${(tester?: ILSUser) => tester?.ecommerce?.fullName ?? tester?.username ?? ''}
            placeholder="Schauspieler auswählen"
            @value-picked=${(e: ValueEvent<ILSUser>) => (this._selectedTester = e.detail.value)}
          ></x-dropdown-selection>
          <x-checkbox
            label="Ersatzschauspieler für den Rest der Runde einsetzen"
            @value-picked=${(e: ValueEvent<boolean>) => (this._replaceAllFutureSlots = e.detail.value)}
          ></x-checkbox>
        </x-flex>
        <x-flex horizontal end space="sm">
          <x-button mode="outline" @click=${() => this.close(false)}> Abbrechen </x-button>
          <x-button @click=${() => this._submit()}> Anwenden </x-button>
        </x-flex>
      </x-flex>
    `;
  }

  private async _submit() {
    const selectedSlot = this.examSlot;
    const examDay = this.examDay.value;
    if (!selectedSlot || !examDay) return;
    const selectedTester = this._selectedTester;
    if (!selectedTester) return XDialog.alert('Es muss einen Schauspieler für das Ersetzen ausgewählt werden!');

    let slotsToUpdate = [selectedSlot];
    if (this._replaceAllFutureSlots) {
      slotsToUpdate = slotsToUpdate.concat(
        this.allSlots.filter(
          slot =>
            slot.tester === selectedSlot.tester &&
            slot.startTime > selectedSlot.startTime &&
            examDay.slotIsInRound(examDay.getRoundNumberForSlot(this.examSlot), slot),
        ),
      );
    }

    slotsToUpdate.forEach(slot => (slot.tester = selectedTester._id));

    try {
      await XDialog.waitFor(
        Promise.all([
          firstValueFrom(timer(500)),
          Promise.all(slotsToUpdate.map(slot => slot.save())).then(() => this.close(true)),
        ]),
      );
    } catch (e) {
      logger.error({ err: e }, 'Es gab ein Problem beim Ersetzen des Schauspielers auf einem Slot');
      return XDialog.alert('Es gab ein Problem beim Ersetzen des Schauspielers. Bitte versuche es später erneut!');
    }
  }
}
